import type { SmoothScrollOptions } from './types';

const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

// Scroll optimization - cache and RAF management
let scrollAnimationFrameId: number | null = null;
let restoreScrollBehavior: (() => void) | null = null;
const scrollCache = new Map<string, number>();

const getNavHeight = () => {
  if (typeof document === 'undefined') {
    return 0;
  }
  const nav = document.querySelector('nav');
  return nav ? nav.getBoundingClientRect().height : 0;
};

const smoothScrollToPosition = (targetPosition: number, options: SmoothScrollOptions = {}) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Cancel any ongoing scroll animation
  if (scrollAnimationFrameId) {
    cancelAnimationFrame(scrollAnimationFrameId);
    scrollAnimationFrameId = null;
  }

  if (restoreScrollBehavior) {
    restoreScrollBehavior();
    restoreScrollBehavior = null;
  }

  const startY = window.scrollY || window.pageYOffset || 0;
  const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
  const navOffset = options.offset ?? getNavHeight();
  const destination = Math.min(Math.max(targetPosition - navOffset, 0), maxScroll);
  const distance = destination - startY;

  // Skip if already at destination
  if (Math.abs(distance) < 1) {
    return;
  }

  // Check for reduced motion preference
  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Calculate optimized duration based on distance
  const duration =
    options.duration ??
    Math.min(1400, Math.max(420, Math.abs(distance) * 0.6));

  // Instant scroll for reduced motion or zero duration
  if (prefersReducedMotion || duration === 0) {
    window.scrollTo({ top: destination, behavior: 'auto' });
    scrollCache.set('lastScrollPosition', destination);
    return;
  }

  // Temporarily disable CSS scroll-behavior
  const root = document.documentElement;
  const body = document.body;
  const previousRootBehavior = root.style.scrollBehavior;
  const previousBodyBehavior = body.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  body.style.scrollBehavior = 'auto';

  restoreScrollBehavior = () => {
    root.style.scrollBehavior = previousRootBehavior;
    body.style.scrollBehavior = previousBodyBehavior;
  };

  let startTime: number | null = null;

  const step = (currentTime: number) => {
    if (!startTime) startTime = currentTime;
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutCubic(progress);
    const currentPosition = startY + distance * easedProgress;

    window.scrollTo(0, currentPosition);
    scrollCache.set('lastScrollPosition', currentPosition);

    if (progress < 1) {
      scrollAnimationFrameId = requestAnimationFrame(step);
    } else {
      scrollAnimationFrameId = null;
      if (restoreScrollBehavior) {
        restoreScrollBehavior();
        restoreScrollBehavior = null;
      }
    }
  };

  scrollAnimationFrameId = requestAnimationFrame(step);
};

const smoothScrollToElement = (element: HTMLElement | null, options?: SmoothScrollOptions) => {
  if (typeof window === 'undefined' || !element) {
    return;
  }
  const targetPosition = element.getBoundingClientRect().top + window.pageYOffset;
  smoothScrollToPosition(targetPosition, options);
};

const smoothScrollToTop = (options?: SmoothScrollOptions) => {
  smoothScrollToPosition(0, { ...options, offset: 0 });
};

export { easeInOutCubic, getNavHeight, smoothScrollToPosition, smoothScrollToElement, smoothScrollToTop, scrollCache };
